import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Cloud, RefreshCw, Unlink, Link2, Layers, ChevronDown, ChevronRight, FileDown, AlertTriangle, History, RotateCcw, Settings2, CheckCircle2, X, Plus, Copy, Trash2, Lock } from "lucide-react";
import { LEAD_CODES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const CATS = ["Income", "Expenditure", "Asset", "Liability"];
const TARGETS = [
    { v: "current_period", label: "Current" },
    { v: "prior_period", label: "Prior" },
    { v: "budget", label: "Budget" },
];
const REASON_LABEL = {
    unclassified: "No category",
    missing_schedule: "No schedule",
    non_numeric: "Bad amount",
    new_account: "New account",
    duplicate: "Duplicate",
};

export default function TrialBalanceImport() {
    const { engagement, reload } = useOutletContext();
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [lock, setLock] = useState(null);
    const canWrite = lock ? lock.holder : true;  // optimistic until first heartbeat; backend 423 is the backstop
    const lockMsg = lock && !lock.holder && lock.locked_by ? `Trial balance is read-only — ${lock.locked_by} is editing it` : "Trial balance is read-only";
    const guard = () => { if (!canWrite) { toast.error(lockMsg); return false; } return true; };
    const [status, setStatus] = useState(null);
    const [settings, setSettings] = useState({ single_schedule: false, schedule_names: [] });
    const [leadDefs, setLeadDefs] = useState(null); // live lead-schedule library (null = not loaded yet)
    const [q, setQ] = useState("");
    const [stmt, setStmt] = useState("all"); // all | ie | bs | attention
    const [xeroEnabled, setXeroEnabled] = useState(false);
    const [params] = useSearchParams();
    const [focusSet, setFocusSet] = useState(() => new Set());
    useEffect(() => { api.get("/settings/integrations").then((r) => setXeroEnabled(!!r.data.xero_enabled)).catch(() => {}); }, []);
    useEffect(() => {
        const f = params.get("focus");
        const ids = (f || "").split(",").filter(Boolean);
        if (!ids.length) return;
        setFocusSet(new Set(ids));
        setStmt("all"); setQ("");
    }, [params]);
    useEffect(() => {
        if (!focusSet.size || !rows.length) return;
        const first = [...focusSet][0];
        const t = setTimeout(() => { document.querySelector(`[data-testid="tb-row-${first}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 350);
        const clear = setTimeout(() => setFocusSet(new Set()), 6000);
        return () => { clearTimeout(t); clearTimeout(clear); };
    }, [focusSet, rows]);

    const load = () => {
        api.get(`/engagements/${engagement.id}/trial-balance`).then((r) => setRows(r.data)).catch(() => {});
        api.get(`/engagements/${engagement.id}/trial-balance/status`).then((r) => setStatus(r.data)).catch(() => {});
        api.get(`/engagements/${engagement.id}/accounts/settings`).then((r) => setSettings(r.data)).catch(() => {});
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [engagement.id]);
    useEffect(() => { api.get("/wp-templates").then((r) => setLeadDefs(r.data.filter((t) => t.is_lead_schedule).map((t) => t.code))).catch(() => setLeadDefs([])); }, []);
    // TB allocation offers only lead sheets that still exist in the firm's Workpaper Setups library.
    const activeLeadCodes = leadDefs == null ? LEAD_CODES.map((l) => l.v) : LEAD_CODES.filter((l) => leadDefs.includes(l.v)).map((l) => l.v);
    const leadOptionsFor = (current) => { const codes = activeLeadCodes.slice(); if (current && !codes.includes(current)) codes.push(current); return codes; };

    // Exclusive TB edit lock: heartbeat while the page is open, release on leave.
    useEffect(() => {
        let alive = true;
        const beat = () => api.post(`/engagements/${engagement.id}/trial-balance/lock`).then((r) => { if (alive) setLock(r.data); }).catch(() => {});
        beat();
        const iv = setInterval(beat, 20000);
        return () => { alive = false; clearInterval(iv); api.post(`/engagements/${engagement.id}/trial-balance/unlock`).catch(() => {}); };
        /* eslint-disable-next-line */
    }, [engagement.id]);

    const takeOver = async () => {
        try { const { data } = await api.post(`/engagements/${engagement.id}/trial-balance/lock/take-over`); setLock(data); toast.success("You now have the trial balance — the other editor is read-only"); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const patchRow = async (r, patch) => {
        if (!guard()) return;
        try {
            const { data } = await api.patch(`/engagements/${engagement.id}/trial-balance/${r.id}`, patch);
            setRows((rs) => rs.map((x) => (x.id === r.id ? data : x)));
            if (data.journals_updated > 0) toast.success(`Account updated — ${data.journals_updated} journal${data.journals_updated > 1 ? "s" : ""} re-linked automatically`);
            api.get(`/engagements/${engagement.id}/trial-balance/status`).then((s) => setStatus(s.data)).catch(() => {});
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const deleteRow = async (r) => {
        if (!guard()) return;
        if (!window.confirm(`Delete "${r.account_name}" from the trial balance?`)) return;
        try {
            await api.delete(`/engagements/${engagement.id}/trial-balance/${r.id}`);
            setRows((rs) => rs.filter((x) => x.id !== r.id));
            api.get(`/engagements/${engagement.id}/trial-balance/status`).then((s) => setStatus(s.data)).catch(() => {});
            reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const addRow = async () => {
        if (!guard()) return;
        try {
            const { data } = await api.post(`/engagements/${engagement.id}/trial-balance/row`, {});
            setRows((rs) => [data, ...rs]);
            api.get(`/engagements/${engagement.id}/trial-balance/status`).then((s) => setStatus(s.data)).catch(() => {});
            toast.success("Blank row added — edit the name and amounts");
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const clearTb = async () => {
        if (!guard()) return;
        if (!window.confirm("Delete the ENTIRE trial balance for this file? A snapshot is kept so you can revert. This cannot be partially undone.")) return;
        try {
            const { data } = await api.delete(`/engagements/${engagement.id}/trial-balance`);
            toast.success(`Trial balance cleared (${data.deleted} rows removed)`);
            load(); reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const dedupe = async () => {
        if (!guard()) return;
        if (!window.confirm("Remove duplicate accounts (keeping the first of each)? A snapshot is kept so you can revert.")) return;
        try {
            const { data } = await api.post(`/engagements/${engagement.id}/trial-balance/dedupe`);
            toast[data.removed ? "success" : "info"](data.removed ? `${data.removed} duplicate row(s) removed` : "No duplicates found");
            load(); reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const exportCsv = async () => {
        try {
            const res = await api.get(`/engagements/${engagement.id}/trial-balance.csv`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a"); a.href = url; a.download = "trial-balance.csv"; a.click(); URL.revokeObjectURL(url);
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const downloadTemplate = async () => {
        try {
            const res = await api.get(`/templates/trial-balance.csv`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a"); a.href = url; a.download = "trial-balance-import-template.csv"; a.click(); URL.revokeObjectURL(url);
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const loadStandard = async () => {
        if (!guard()) return;
        if (!window.confirm("Add the generic service-charge chart of accounts to this file? Accounts already present are left untouched; you can delete any you don't need.")) return;
        try {
            const { data } = await api.post(`/engagements/${engagement.id}/trial-balance/prefill-standard`);
            toast.success(`${data.added} standard account(s) added`);
            load(); reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const scheduleOptions = Array.from(new Set([...(settings.schedule_names || []), ...rows.map((r) => r.schedule).filter(Boolean), "Unassigned"]));
    const attentionCount = rows.filter((r) => (r.attention || []).length).length;
    const rowHasFigure = (r) => Math.abs(Number(r.current_period) || 0) > 0 || Math.abs(Number(r.prior_period) || 0) > 0 || Math.abs(Number(r.budget) || 0) > 0;
    const unmappedCount = rows.filter((r) => !r.lead_code && rowHasFigure(r)).length;
    const totalCurrent = rows.reduce((s, r) => s + Number(r.current_period || 0), 0);
    const totalPrior = rows.reduce((s, r) => s + Number(r.prior_period || 0), 0);
    const totalBudget = rows.reduce((s, r) => s + Number(r.budget || 0), 0);

    const leadLabel = (code) => (LEAD_CODES.find((l) => l.v === code) || {}).label || "";
    const IE_CATS = ["Income", "Expenditure"];
    const term = q.trim().toLowerCase();
    const visibleRows = rows.filter((r) => {
        if (stmt === "ie" && !IE_CATS.includes(r.category)) return false;
        if (stmt === "bs" && IE_CATS.includes(r.category)) return false; // BS = everything not I&E (Asset/Liability/Reserve/blank)
        if (stmt === "attention" && !(r.attention || []).length) return false;
        if (stmt === "unmapped" && (r.lead_code || !rowHasFigure(r))) return false;
        if (!term) return true;
        const hay = [r.account_name, r.account_code, r.schedule, r.category, leadLabel(r.lead_code),
            r.current_period, r.prior_period, r.budget].map((x) => String(x ?? "").toLowerCase()).join(" ");
        return hay.includes(term);
    });

    return (
        <div className="p-6 max-w-6xl" data-testid="tb-import-page">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-display mb-1">Trial balance</div>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-5">Import & manage</h2>

            {lock && !lock.holder && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-sm px-4 py-3 mb-5 flex items-start gap-3" data-testid="tb-editing-lock-banner">
                    <Lock className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <div className="flex-1 text-xs text-amber-900">
                        <span className="font-display font-bold">{lock.locked_by || "Another user"} is editing the trial balance.</span> It's read-only for you until they finish — it frees up automatically after 5 minutes of inactivity.
                    </div>
                    {user?.role === "Partner" && (
                        <button data-testid="tb-take-over-btn" onClick={takeOver} className="text-xs font-semibold bg-amber-700 hover:bg-amber-800 text-white px-3 py-1 rounded-sm shrink-0">Take over</button>
                    )}
                </div>
            )}

            {status?.locked && (
                <div className="bg-red-50 border-2 border-red-300 rounded-sm px-4 py-3 mb-5 flex items-start gap-3" data-testid="tb-lock-banner">
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1 text-xs text-red-900">
                        <span className="font-display font-bold">Trial balance needs attention — lead sheets &amp; the Accounts module are locked.</span>
                        <ul className="mt-1 list-disc ml-4 space-y-0.5">
                            {status.attention_count > 0 && <li>{status.attention_count} row{status.attention_count > 1 ? "s" : ""} need a category, schedule or a valid amount (highlighted below).</li>}
                            {status.unbalanced_columns?.map((c) => (
                                <li key={c}>{colLabel(c)} does not balance — debits less credits = {fmtGbp(status.balance[c].sum)} (must be £0.00).</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <ScheduleSetup engagement={engagement} settings={settings} setSettings={setSettings} onChanged={load} />

            {xeroEnabled && <XeroConnect engagement={engagement} reload={reload} onImported={() => { load(); reload?.(); }} />}

            <ImportPanel engagementId={engagement.id} readOnly={!canWrite} onImported={() => { load(); reload?.(); }}
                onDownloadTemplate={downloadTemplate} onLoadStandard={loadStandard} />

            <TbSnapshots engagement={engagement} readOnly={!canWrite} reload={() => { load(); reload?.(); }} />

            <div className="bg-white border border-border rounded-sm">
                <div className="px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground flex items-center flex-wrap gap-2">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Trial balance ({rows.length} rows{attentionCount > 0 ? <span className="text-red-600 normal-case tracking-normal font-semibold"> · {attentionCount} need attention</span> : ""}{unmappedCount > 0 ? <span className="text-risk-med normal-case tracking-normal font-semibold" data-testid="tb-unmapped-count"> · {unmappedCount} unmapped</span> : ""})
                    <div className="ml-auto font-num text-xs flex items-center gap-4 flex-wrap">
                        <span>Current: <span className={totalCurrent < 0 ? "text-num-neg" : "text-num-pos"}>{fmtGbp(totalCurrent)}</span></span>
                        <span>Prior: <span className={totalPrior < 0 ? "text-num-neg" : "text-num-pos"}>{fmtGbp(totalPrior)}</span></span>
                        <span>Budget: <span className={totalBudget < 0 ? "text-num-neg" : "text-num-pos"}>{fmtGbp(totalBudget)}</span></span>
                        <button data-testid="tb-add-row" onClick={addRow} disabled={!canWrite} title="Add a blank row (key a TB in manually)" className="flex items-center gap-1 normal-case tracking-normal font-medium text-brand hover:bg-brand/5 border border-brand/30 rounded-sm px-2 py-1 disabled:opacity-40"><Plus className="w-3.5 h-3.5" /> Add row</button>
                        {rows.length > 0 && <button data-testid="tb-dedupe-btn" onClick={dedupe} disabled={!canWrite} title="Remove duplicate accounts (keeps the first of each)" className="flex items-center gap-1 normal-case tracking-normal font-medium text-brand hover:bg-brand/5 border border-brand/30 rounded-sm px-2 py-1 disabled:opacity-40"><Copy className="w-3.5 h-3.5" /> Remove duplicates</button>}
                        {rows.length > 0 && (
                            <button data-testid="tb-export-btn" onClick={exportCsv} title="Export this trial balance to CSV" className="flex items-center gap-1 normal-case tracking-normal font-medium text-brand hover:bg-brand/5 border border-brand/30 rounded-sm px-2 py-1">
                                <FileDown className="w-3.5 h-3.5" /> Export CSV
                            </button>
                        )}
                        {rows.length > 0 && <button data-testid="tb-clear-btn" onClick={clearTb} disabled={!canWrite} title="Delete the entire trial balance (revertable via snapshot)" className="flex items-center gap-1 normal-case tracking-normal font-medium text-red-600 hover:bg-red-50 border border-red-200 rounded-sm px-2 py-1 disabled:opacity-40"><Trash2 className="w-3.5 h-3.5" /> Clear all</button>}
                    </div>
                </div>
                <div className="px-4 py-2 border-b border-border flex items-center flex-wrap gap-2 bg-slate-50/60" data-testid="tb-filter-bar">
                    <div className="relative">
                        <input data-testid="tb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search account, amount, schedule…"
                            className="w-64 border border-input rounded-sm pl-7 pr-2 py-1.5 text-xs bg-white" />
                        <svg className="w-3.5 h-3.5 absolute left-2 top-2 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
                    </div>
                    {[["all", "All"], ["ie", "Income & expenditure"], ["bs", "Balance sheet"], ["attention", `Needs attention${attentionCount ? ` (${attentionCount})` : ""}`], ["unmapped", `Unmapped${unmappedCount ? ` (${unmappedCount})` : ""}`]].map(([v, label]) => (
                        <button key={v} data-testid={`tb-filter-${v}`} onClick={() => setStmt(v)}
                            className={`text-xs px-2.5 py-1.5 rounded-sm border ${stmt === v ? "bg-brand text-white border-brand" : "bg-white border-input hover:bg-slate-100"} ${v === "unmapped" && unmappedCount ? "text-risk-med" : ""}`}>{label}</button>
                    ))}
                    {(term || stmt !== "all") && <span className="text-[11px] text-muted-foreground ml-1">Showing {visibleRows.length} of {rows.length}</span>}
                    {(term || stmt !== "all") && <button data-testid="tb-filter-clear" onClick={() => { setQ(""); setStmt("all"); }} className="text-[11px] text-brand hover:underline">Clear</button>}
                </div>
                <div className="overflow-auto max-h-[520px]">
                    <table className="tbl">
                        <thead className="sticky top-0">
                            <tr>
                                <th>Account</th>
                                <th className="w-44">Schedule</th>
                                <th className="w-32">Category</th>
                                <th className="w-48">Lead sheet</th>
                                <th className="text-right">Current</th>
                                <th className="text-right">Prior</th>
                                <th className="text-right">Budget</th>
                                <th className="w-8"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="tb-rows">
                            {visibleRows.map((r, i) => {
                                const att = r.attention || [];
                                const flagged = att.length > 0;
                                return (
                                    <tr key={r.id || i} className={`${flagged ? "bg-red-50" : ""} ${focusSet.has(r.id) ? "ring-2 ring-amber-500 ring-inset" : ""}`} data-testid={`tb-row-${r.id}`}>
                                        <td>
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <input data-testid={`tb-code-${r.id}`} defaultValue={r.account_code || ""} placeholder="code" title="Account code"
                                                    onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.account_code || "")) patchRow(r, { account_code: v }); }}
                                                    className="border border-input rounded-sm px-1.5 py-0.5 text-[10px] font-num bg-white w-16 text-muted-foreground" />
                                                <input data-testid={`tb-name-${r.id}`} defaultValue={r.account_name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.account_name) patchRow(r, { account_name: v }); }}
                                                    className={`border border-input rounded-sm px-1.5 py-0.5 text-xs bg-white min-w-[180px] ${flagged ? "text-red-900 font-medium" : ""}`} />
                                                {r.is_new && <span title="New account from the latest import" className="text-[9px] uppercase tracking-wider font-display font-bold text-brand bg-brand/10 rounded-sm px-1 py-0.5">New</span>}
                                                {att.map((a) => <span key={a} data-testid={`tb-flag-${a}`} className="text-[9px] uppercase tracking-wider font-display font-bold text-red-700 bg-red-100 rounded-sm px-1 py-0.5">{REASON_LABEL[a] || a}</span>)}
                                            </div>
                                            {IE_CATS.includes(r.category) && (
                                                <label title="Show this account in the 'Contribution to / (from) Reserves' block on the Income & Expenditure account, split out from normal income/expenditure." className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer">
                                                    <input type="checkbox" data-testid={`tb-reserve-contrib-${r.id}`} className="accent-brand w-3 h-3" checked={!!r.reserve_contribution} onChange={(e) => patchRow(r, { reserve_contribution: e.target.checked })} />
                                                    Reserve contribution
                                                </label>
                                            )}
                                            {!IE_CATS.includes(r.category) && (
                                                <label title="Tag this balance-sheet account as directly related to the surplus/deficit fund, so its balance feeds the fund note reconciliation (e.g. a brought-forward 'Surplus repayable to leaseholders' account)." className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer">
                                                    <input type="checkbox" data-testid={`tb-fund-related-${r.id}`} className="accent-brand w-3 h-3" checked={!!r.fund_related} onChange={(e) => patchRow(r, { fund_related: e.target.checked })} />
                                                    Fund-related (surplus/deficit)
                                                </label>
                                            )}
                                        </td>
                                        <td>
                                            {settings.single_schedule ? <span className="text-xs text-muted-foreground italic">single schedule</span> : (
                                                <input data-testid={`tb-sched-${r.id}`} list="tb-sched-opts" defaultValue={r.schedule || ""} onBlur={(e) => { if ((e.target.value || "") !== (r.schedule || "")) patchRow(r, { schedule: e.target.value }); }}
                                                    className={`w-full border rounded-sm px-1.5 py-0.5 text-xs bg-white ${att.includes("missing_schedule") ? "border-red-400" : "border-input"}`} />
                                            )}
                                        </td>
                                        <td>
                                            <select data-testid={`tb-cat-${r.id}`} value={r.category || ""} onChange={(e) => patchRow(r, { category: e.target.value })}
                                                className={`w-full border rounded-sm px-1 py-0.5 text-xs bg-white ${att.includes("unclassified") ? "border-red-400" : "border-input"}`}>
                                                <option value="">— pick —</option>
                                                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </td>
                                        <td>
                                            <select data-testid={`tb-lead-${r.id}`} value={r.lead_code || ""} onChange={(e) => patchRow(r, { lead_code: e.target.value || null })} className={`w-full border rounded-sm px-1 py-0.5 text-xs bg-white ${r.lead_code ? "border-input" : "border-risk-med"}`}>
                                                <option value="">— unmapped —</option>
                                                {leadOptionsFor(r.lead_code).map((code) => <option key={code} value={code}>{leadLabel(code) || code}</option>)}
                                            </select>
                                        </td>
                                        <td className="num"><input data-testid={`tb-cur-${r.id}`} defaultValue={r.current_period} onBlur={(e) => { if (String(e.target.value) !== String(r.current_period)) patchRow(r, { current_period: e.target.value }); }} className={`w-24 border border-input rounded-sm px-1 py-0.5 text-xs text-right bg-white ${r.current_period < 0 ? "num-neg" : ""}`} /></td>
                                        <td className="num"><input data-testid={`tb-pri-${r.id}`} defaultValue={r.prior_period} onBlur={(e) => { if (String(e.target.value) !== String(r.prior_period)) patchRow(r, { prior_period: e.target.value }); }} className={`w-24 border border-input rounded-sm px-1 py-0.5 text-xs text-right bg-white ${r.prior_period < 0 ? "num-neg" : ""}`} /></td>
                                        <td className="num"><input data-testid={`tb-bud-${r.id}`} defaultValue={r.budget} onBlur={(e) => { if (String(e.target.value) !== String(r.budget)) patchRow(r, { budget: e.target.value }); }} className={`w-24 border border-input rounded-sm px-1 py-0.5 text-xs text-right bg-white ${r.budget < 0 ? "num-neg" : ""}`} /></td>
                                        <td className="text-right"><button data-testid={`tb-del-${r.id}`} onClick={() => deleteRow(r)} title="Delete this row" className="text-muted-foreground hover:text-red-600"><X className="w-4 h-4" /></button></td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8 italic">No trial balance loaded yet. Import a CSV above, or use “Add row” to key one in.</td></tr>}
                            {rows.length > 0 && visibleRows.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-8 italic">No rows match your filter.</td></tr>}
                        </tbody>
                    </table>
                    <datalist id="tb-sched-opts">{scheduleOptions.map((s) => <option key={s} value={s} />)}</datalist>
                </div>
            </div>
        </div>
    );
}

function colLabel(c) {
    return { current_period: "Current", prior_period: "Prior", budget: "Budget" }[c] || c;
}

const MAP_FIELDS = [
    ["account_name", "Account name *"],
    ["account_code", "Account code"],
    ["schedule", "Schedule"],
    ["category", "Category"],
    ["current_period", "Current amount"],
    ["prior_period", "Prior amount"],
    ["budget", "Budget amount"],
    ["debit", "Debit"],
    ["credit", "Credit"],
];

function splitCsvLine(l) {
    const out = []; let cur = ""; let inq = false;
    for (const ch of l) {
        if (ch === '"') inq = !inq;
        else if (ch === "," && !inq) { out.push(cur); cur = ""; }
        else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim().replace(/^"|"$/g, ""));
}

function ImportPanel({ engagementId, onImported, onDownloadTemplate, onLoadStandard, readOnly }) {
    const [csvText, setCsvText] = useState("");
    const [headers, setHeaders] = useState(null);
    const [dataRows, setDataRows] = useState([]);
    const [map, setMap] = useState({});
    const [dcPeriod, setDcPeriod] = useState("current_period");
    const [replace, setReplace] = useState(false);
    const [busy, setBusy] = useState(false);

    const readColumns = () => {
        const text = csvText.trim();
        if (!text) return toast.error("Paste or choose a CSV first");
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) return toast.error("Need a header row plus at least one data row");
        const hdr = splitCsvLine(lines[0]);
        setHeaders(hdr);
        setDataRows(lines.slice(1).map(splitCsvLine));
        const find = (...names) => hdr.findIndex((h) => names.includes(h.trim().toLowerCase()));
        setMap({
            account_name: find("account_name", "account", "name"),
            account_code: find("account_code", "code"),
            schedule: find("schedule", "service charge area", "tracking"),
            category: find("category", "type"),
            current_period: find("current_period", "current", "current year", "amount"),
            prior_period: find("prior_period", "prior", "prior year"),
            budget: find("budget"),
            debit: find("debit", "debit gbp"),
            credit: find("credit", "credit gbp"),
        });
    };

    const onFile = (ev) => {
        const f = ev.target.files?.[0];
        if (!f) return;
        readCsv(f);
        ev.target.value = "";
    };
    const readCsv = (f) => {
        if (!f) return;
        if (!/\.csv$/i.test(f.name) && f.type && !/csv|text/.test(f.type)) return toast.error("Please choose a .csv file");
        const rdr = new FileReader();
        rdr.onload = () => { setCsvText(String(rdr.result || "")); toast.success(`Loaded ${f.name}`); };
        rdr.onerror = () => toast.error("Could not read that file");
        rdr.readAsText(f);
    };
    const [drag, setDrag] = useState(false);
    const onDrop = (ev) => { ev.preventDefault(); setDrag(false); readCsv(ev.dataTransfer.files?.[0]); };

    const num = (raw) => {
        let s = String(raw ?? "").replace(/[£,\s]/g, "");
        if (!s || s === "-") return 0;
        const neg = s.startsWith("(") && s.endsWith(")");
        if (neg) s = s.slice(1, -1);
        const n = parseFloat(s);
        return isNaN(n) ? "" : (neg ? -n : n);
    };
    const cell = (c, idx) => (idx >= 0 ? (c[idx] ?? "").trim() : "");

    const doImport = async () => {
        if (readOnly) { toast.error("Trial balance is read-only — another user is editing it"); return; }
        if ((map.account_name ?? -1) < 0) return toast.error("Map the Account name column");
        const hasDC = (map.debit ?? -1) >= 0 || (map.credit ?? -1) >= 0;
        const periodCols = ["current_period", "prior_period", "budget"].filter((t) => (map[t] ?? -1) >= 0);
        const targets = [...periodCols];
        if (hasDC && !targets.includes(dcPeriod)) targets.push(dcPeriod);
        if (!targets.length) return toast.error("Map at least one amount column: Current, Prior, Budget, or Debit/Credit");
        const outRows = dataRows.map((c) => {
            const row = {
                account_code: cell(c, map.account_code ?? -1),
                account_name: cell(c, map.account_name ?? -1),
                schedule: cell(c, map.schedule ?? -1),
                category: cell(c, map.category ?? -1),
            };
            periodCols.forEach((t) => { row[t] = cell(c, map[t]); });
            if (hasDC) row[dcPeriod] = (num(cell(c, map.debit ?? -1)) || 0) - (num(cell(c, map.credit ?? -1)) || 0);
            return row;
        }).filter((r) => r.account_name);
        if (!outRows.length) return toast.error("No data rows with an account name");
        setBusy(true);
        try {
            const { data } = await api.post(`/engagements/${engagementId}/trial-balance/import`, { rows: outRows, targets, replace });
            const tl = targets.map(colLabel).join(", ");
            if (data.locked) toast.warning(`Imported ${data.imported} rows (${tl})${data.unbalanced_columns.length ? ` — ${data.unbalanced_columns.map(colLabel).join(" & ")} out of balance` : ""}${data.attention_count ? `, ${data.attention_count} need attention` : ""}. Locked until fixed.`, { duration: 9000 });
            else toast.success(`Imported ${data.imported} rows (${tl}) — all balanced`);
            setCsvText(""); setHeaders(null); setDataRows([]); setMap({}); setReplace(false);
            onImported?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const colOptions = (headers || []).map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>);
    const hasDC = (map.debit ?? -1) >= 0 || (map.credit ?? -1) >= 0;

    return (
        <div className="bg-white border border-border rounded-sm mb-5" data-testid="tb-import-panel">
            <div className="px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground flex items-center gap-2">
                <Upload className="w-3.5 h-3.5" /> Import CSV
            </div>
            <div className="p-4 space-y-3">
                <div className="relative" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
                    <textarea data-testid="tb-csv-textarea" rows={5} value={csvText} onChange={(e) => setCsvText(e.target.value)}
                        className={`w-full border rounded-sm px-3 py-2 text-xs font-mono bg-slate-50 ${drag ? "border-brand ring-2 ring-brand/30" : "border-input"}`}
                        placeholder={"Drag a .csv here, or paste / choose a file (any column order). Example:\nCode,Account,Area,Type,Current,Prior,Budget\n100,Service charge,Apartments,Income,-5000,-4800,-5000\n420,Insurance,Apartments,Expenditure,1200,1150,1300"} />
                    {drag && <div className="absolute inset-0 flex items-center justify-center bg-brand/5 border-2 border-dashed border-brand rounded-sm text-brand text-xs font-medium pointer-events-none" data-testid="tb-drop-overlay"><Upload className="w-4 h-4 mr-1.5" /> Drop CSV to load</div>}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <label className="text-xs border border-border hover:bg-slate-50 rounded-sm px-3 py-2 cursor-pointer flex items-center gap-1.5" data-testid="tb-file-label">
                        <Upload className="w-3.5 h-3.5" /> Choose CSV file
                        <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="tb-file-input" />
                    </label>
                    <button data-testid="tb-read-columns" onClick={readColumns} disabled={!csvText.trim()} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-40">Read columns</button>
                    <span className="text-muted-foreground text-xs">·</span>
                    <button data-testid="tb-download-template" onClick={onDownloadTemplate} className="text-xs border border-border hover:bg-slate-50 rounded-sm px-3 py-2 flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> Download blank template</button>
                    <button data-testid="tb-load-standard" onClick={onLoadStandard} className="text-xs border border-border hover:bg-slate-50 rounded-sm px-3 py-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Load standard accounts</button>
                </div>

                {headers && (
                    <div className="border border-border rounded-sm p-3 bg-slate-50/60 space-y-3" data-testid="tb-mapping">
                        <div className="text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground">Match your columns</div>
                        <p className="text-[11px] text-muted-foreground">Tell us which of your columns is which. Map <b>Current</b>, <b>Prior</b> and/or <b>Budget</b> to import them all in one go — or just one. Amounts are treated as signed (income/credits negative). If your file has Debit & Credit columns instead, map those and pick which period they belong to.</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                            {MAP_FIELDS.map(([f, label]) => (
                                <label key={f} className="text-xs flex flex-col gap-1">
                                    <span className="text-muted-foreground">{label}</span>
                                    <select data-testid={`tb-map-${f}`} value={map[f] ?? -1} onChange={(e) => setMap((m) => ({ ...m, [f]: parseInt(e.target.value, 10) }))}
                                        className="border border-input rounded-sm px-1.5 py-1 text-xs bg-white">
                                        <option value={-1}>— none —</option>
                                        {colOptions}
                                    </select>
                                </label>
                            ))}
                        </div>
                        {hasDC && (
                            <label className="text-xs flex items-center gap-2" data-testid="tb-dc-period-wrap">
                                <span className="text-muted-foreground">Debit/Credit columns are for:</span>
                                <select data-testid="tb-dc-period" value={dcPeriod} onChange={(e) => setDcPeriod(e.target.value)} className="border border-input rounded-sm px-1.5 py-1 text-xs bg-white">
                                    {TARGETS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                                </select>
                            </label>
                        )}
                        <div className="flex flex-wrap items-center gap-3 pt-1">
                            <label className="text-xs flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" data-testid="tb-replace" className="accent-brand w-4 h-4" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
                                <span><b>Replace the whole trial balance</b> (wipes all existing rows first — no leftover duplicates)</span>
                            </label>
                            <button data-testid="tb-import-btn" onClick={doImport} disabled={busy || readOnly} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-40 ml-auto">{busy ? "Importing…" : (replace ? "Replace & import" : "Import (merge)")}</button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">{dataRows.length} data row(s) detected. Rows with a missing category, schedule or amount still import and are highlighted below to fix in-system.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function ScheduleSetup({ engagement, settings, setSettings, onChanged }) {
    const [open, setOpen] = useState(false);
    const [names, setNames] = useState((settings.schedule_names || []).join("\n"));
    useEffect(() => { setNames((settings.schedule_names || []).join("\n")); }, [settings.schedule_names]);

    const setSingle = async (single) => {
        try {
            await api.patch(`/engagements/${engagement.id}/accounts/settings`, { single_schedule: single });
            setSettings((s) => ({ ...s, single_schedule: single }));
            toast.success(single ? "Set to single schedule — blank schedules won't be flagged" : "Set to multiple schedules");
            onChanged?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    const saveNames = async () => {
        const list = names.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        try {
            await api.patch(`/engagements/${engagement.id}/accounts/settings`, { schedule_names: list });
            setSettings((s) => ({ ...s, schedule_names: list }));
            toast.success(`${list.length} schedule name${list.length === 1 ? "" : "s"} saved`);
            onChanged?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    return (
        <div className="bg-white border border-border rounded-sm mb-5" data-testid="tb-schedule-setup">
            <button onClick={() => setOpen(!open)} className="w-full px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground flex items-center gap-2">
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}<Settings2 className="w-3.5 h-3.5" /> Schedule setup
                <span className="ml-2 normal-case tracking-normal font-normal text-brand">{settings.single_schedule ? "Single schedule" : `${(settings.schedule_names || []).length} named schedule(s)`}</span>
            </button>
            {open && (
                <div className="p-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">Does this file have separate service-charge schedules?</span>
                        <button data-testid="tb-single-off" onClick={() => setSingle(false)} className={`text-xs px-3 py-1.5 rounded-sm border ${!settings.single_schedule ? "bg-brand text-white border-brand" : "bg-white border-input"}`}>Yes — multiple schedules</button>
                        <button data-testid="tb-single-on" onClick={() => setSingle(true)} className={`text-xs px-3 py-1.5 rounded-sm border ${settings.single_schedule ? "bg-brand text-white border-brand" : "bg-white border-input"}`}>No — single schedule</button>
                    </div>
                    {!settings.single_schedule && (
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-display">Schedule names (one per line)</label>
                            <textarea data-testid="tb-sched-names" rows={4} value={names} onChange={(e) => setNames(e.target.value)} className="w-full border border-input rounded-sm px-2 py-1.5 text-xs bg-white mt-1" placeholder={"Schedule 1 - Estate\nSchedule 2 - Apartments"} />
                            <button data-testid="tb-sched-names-save" onClick={saveNames} className="mt-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-sm">Save schedule names</button>
                            <p className="text-[11px] text-muted-foreground mt-1">In multi-schedule mode, income &amp; expenditure rows without a schedule are flagged. Balance-sheet rows (assets/liabilities) never need one.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function TbSnapshots({ engagement, reload, readOnly }) {
    const [open, setOpen] = useState(false);
    const [snaps, setSnaps] = useState(null);
    const load = () => api.get(`/engagements/${engagement.id}/trial-balance/snapshots`).then((r) => setSnaps(r.data)).catch(() => setSnaps([]));
    const toggle = () => { const n = !open; setOpen(n); if (n && snaps === null) load(); };
    const revert = async (s) => {
        if (readOnly) { toast.error("Trial balance is read-only — another user is editing it"); return; }
        if (!window.confirm(`Revert the trial balance to the snapshot from ${new Date(s.at).toLocaleString("en-GB")}?\n\nThis replaces the current TB with that version (${s.count} rows). A snapshot of the current state is taken first, so this can be undone.`)) return;
        try {
            const { data } = await api.post(`/engagements/${engagement.id}/trial-balance/snapshots/${s.id}/revert`);
            toast.success(`Reverted — ${data.restored} rows restored`);
            load(); reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    const exportSnap = async (s) => {
        try {
            const res = await api.get(`/engagements/${engagement.id}/trial-balance/snapshots/${s.id}.csv`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a"); a.href = url; a.download = `tb-snapshot-${s.at.slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    return (
        <div className="bg-white border border-border rounded-sm mb-5" data-testid="tb-snapshots">
            <button onClick={toggle} className="w-full px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground flex items-center gap-2">
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}<History className="w-3.5 h-3.5" /> Import history &amp; revert
                <span className="ml-2 normal-case tracking-normal font-normal text-muted-foreground">Undo a wrong import</span>
            </button>
            {open && (
                <div className="p-4">
                    {snaps === null ? <div className="text-xs text-muted-foreground">Loading…</div> : snaps.length === 0 ? (
                        <div className="text-xs text-muted-foreground italic">No snapshots yet — a snapshot is taken automatically before each import so you can roll back.</div>
                    ) : (
                        <table className="tbl">
                            <thead><tr><th>When</th><th>Change</th><th className="text-right w-16">Rows</th><th className="w-40"></th></tr></thead>
                            <tbody>
                                {snaps.map((s) => (
                                    <tr key={s.id} data-testid={`tb-snap-${s.id}`}>
                                        <td className="text-xs font-num">{new Date(s.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</td>
                                        <td className="text-xs">{s.label}{s.by ? <span className="text-muted-foreground"> · {s.by}</span> : ""}</td>
                                        <td className="num text-xs">{s.count}</td>
                                        <td>
                                            <div className="flex items-center gap-2 justify-end">
                                                <button data-testid={`tb-snap-export-${s.id}`} onClick={() => exportSnap(s)} className="text-xs text-brand hover:underline flex items-center gap-1"><FileDown className="w-3 h-3" /> CSV</button>
                                                {!engagement.archived && <button data-testid={`tb-snap-revert-${s.id}`} onClick={() => revert(s)} disabled={readOnly} className="text-xs text-destructive hover:underline flex items-center gap-1 disabled:opacity-40"><RotateCcw className="w-3 h-3" /> Revert</button>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

function XeroConnect({ engagement, reload, onImported }) {
    const [status, setStatus] = useState({ connected: false, configured: false });
    const [connections, setConnections] = useState([]);
    const [orgsLoaded, setOrgsLoaded] = useState(false);
    const [loadingConns, setLoadingConns] = useState(false);
    const [sel, setSel] = useState("");
    const [busy, setBusy] = useState("");
    const [params, setParams] = useSearchParams();
    const active = engagement.xero_connection_id;       // currently connected to this file, ready to pull
    const remembered = engagement.xero_tenant_id;       // org association known (may be disconnected)

    const loadConnections = async () => {
        setLoadingConns(true);
        try { const { data } = await api.get("/xero/connections"); setConnections(data); setOrgsLoaded(true); return data; }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); return []; }
        finally { setLoadingConns(false); }
    };

    const pullToast = (data) => {
        const bud = data.budget_pulled ? "budget included" : "budget from CSV";
        if (data.changes > 0) toast.warning(`Pulled ${data.pulled} accounts (${bud}): ${data.changes} figure${data.changes > 1 ? "s" : ""} changed. Lead sheets flagged: ${data.flagged_papers.join(", ")}`, { duration: 8000 });
        else toast.success(`Pulled ${data.pulled} accounts from ${data.tenant} — ${bud}${data.disconnected ? " · disconnected" : ""}`);
    };

    const autoRelinkAndPull = async (tenantId) => {
        setBusy("refresh");
        try {
            const conns = await loadConnections();
            const c = conns.find((c) => c.tenant_id === tenantId);
            if (!c) { toast.message("Almost there — select the organisation below to finish reconnecting"); return; }
            await api.post(`/engagements/${engagement.id}/xero/link`, { tenant_id: c.tenant_id, tenant_name: c.tenant_name, connection_id: c.connection_id });
            const { data } = await api.post(`/engagements/${engagement.id}/xero/pull-tb`);
            pullToast(data);
            reload?.(); onImported?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    useEffect(() => {
        const x = params.get("xero");
        if (x) { params.delete("xero"); setParams(params, { replace: true }); }
        if (x === "error") toast.error("Xero connection failed or was cancelled");
        api.get("/xero/status").then(async (r) => {
            setStatus(r.data);
            if (!r.data.connected) return;
            if (x === "connected") {
                if (remembered && !active) { await autoRelinkAndPull(remembered); }
                else if (!active) { await loadConnections(); }
            } else if (!remembered && !active) {
                await loadConnections();
            }
        }).catch(() => {});
        // eslint-disable-next-line
    }, [engagement.id]);

    const connect = async () => {
        try { const { data } = await api.get(`/xero/authorize?eid=${engagement.id}`); window.location.href = data.url; }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const link = async () => {
        const c = connections.find((c) => c.tenant_id === sel);
        if (!c) return;
        setBusy("link");
        try {
            await api.post(`/engagements/${engagement.id}/xero/link`, { tenant_id: c.tenant_id, tenant_name: c.tenant_name, connection_id: c.connection_id });
            toast.success(`Linked to ${c.tenant_name}`);
            reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const pull = async () => {
        setBusy("pull");
        try { const { data } = await api.post(`/engagements/${engagement.id}/xero/pull-tb`); pullToast(data); reload?.(); onImported?.(); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const disconnect = async (forget = false) => {
        setBusy(forget ? "forget" : "disc");
        try {
            await api.post(`/engagements/${engagement.id}/xero/disconnect${forget ? "?forget=true" : ""}`);
            toast.success(forget ? "Organisation removed" : "Disconnected");
            reload?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    if (!status.configured) return null;
    const lastPull = engagement.xero_last_pull_at ? new Date(engagement.xero_last_pull_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : null;

    return (
        <div className="bg-white border border-brand/30 rounded-sm mb-5" data-testid="xero-panel">
            <div className="px-4 py-2.5 border-b border-border text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground flex items-center gap-2">
                <Cloud className="w-3.5 h-3.5 text-brand" /> Xero
                {remembered && <span className="ml-2 normal-case tracking-normal text-brand font-semibold" data-testid="xero-linked-name">· {engagement.xero_tenant_name}</span>}
                {lastPull && <span className="ml-auto normal-case tracking-normal text-muted-foreground font-normal" data-testid="xero-last-pull">Last pull: {lastPull}</span>}
            </div>
            <div className="p-4">
                {busy === "refresh" ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="xero-refreshing"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Reconnecting and pulling the latest trial balance…</div>
                ) : active ? (
                    <>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-muted-foreground flex-1 min-w-[200px]">Connected to <span className="font-semibold text-foreground">{engagement.xero_tenant_name}</span>. Pull imports the current{engagement.has_prior_period ? " and prior-year" : ""} trial balance{engagement.has_budget ? " and budget" : ""}, then frees the Xero connection.</span>
                        <button data-testid="xero-pull-btn" onClick={pull} disabled={busy} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-40 flex items-center gap-1.5">
                            <RefreshCw className={`w-3.5 h-3.5 ${busy === "pull" ? "animate-spin" : ""}`} />{busy === "pull" ? "Pulling…" : "Pull trial balance from Xero"}
                        </button>
                        <button data-testid="xero-disconnect-btn" onClick={() => disconnect(false)} disabled={busy} className="border border-border hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded-sm disabled:opacity-40 flex items-center gap-1.5 text-muted-foreground">
                            <Unlink className="w-3.5 h-3.5" />Disconnect
                        </button>
                    </div>
                    <SchedulesBudgets engagement={engagement} reload={reload} />
                    </>
                ) : remembered ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-muted-foreground flex-1 min-w-[200px]">This file is linked to <span className="font-semibold text-foreground">{engagement.xero_tenant_name}</span> (currently disconnected). Refresh reconnects and re-pulls the latest figures{engagement.has_budget ? " and budget" : ""} from Xero.</span>
                        <button data-testid="xero-refresh-btn" onClick={connect} disabled={busy} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-40 flex items-center gap-1.5">
                            <RefreshCw className="w-3.5 h-3.5" />Refresh from Xero
                        </button>
                        <button data-testid="xero-forget-btn" onClick={() => disconnect(true)} disabled={busy} className="text-muted-foreground hover:text-destructive text-xs">Change organisation</button>
                    </div>
                ) : status.connected ? (
                    loadingConns && !orgsLoaded ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="xero-orgs-loading"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading your Xero organisations…</div>
                    ) : connections.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-muted-foreground">Choose this client's Xero organisation:</span>
                            <select data-testid="xero-org-select" value={sel} onChange={(e) => setSel(e.target.value)} className="border border-input rounded-sm px-2 py-1.5 text-sm bg-white min-w-[240px]">
                                <option value="">— select organisation —</option>
                                {connections.map((c) => <option key={c.tenant_id} value={c.tenant_id}>{c.tenant_name}</option>)}
                            </select>
                            <button data-testid="xero-link-btn" onClick={link} disabled={!sel || busy} className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-40 flex items-center gap-1.5">
                                <Link2 className="w-3.5 h-3.5" />Link
                            </button>
                            <button data-testid="xero-refresh-orgs" onClick={loadConnections} className="text-brand hover:underline text-xs">Refresh list</button>
                            <button data-testid="xero-add-org" onClick={connect} className="text-brand hover:underline text-xs">Not listed? Authorise another organisation</button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2" data-testid="xero-no-orgs">
                            <span className="text-xs text-muted-foreground flex-1 min-w-[220px]">Your Xero login is connected, but <span className="font-semibold text-foreground">no organisations are authorised for it yet</span>. Open Xero to grant this app access to the client's organisation, then it will appear here to link.</span>
                            <button data-testid="xero-choose-org-btn" onClick={connect} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm flex items-center gap-1.5">
                                <Cloud className="w-3.5 h-3.5" />Choose organisation in Xero
                            </button>
                            <button data-testid="xero-refresh-orgs" onClick={loadConnections} className="text-brand hover:underline text-xs">Refresh list</button>
                        </div>
                    )
                ) : (
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs text-muted-foreground flex-1 min-w-[200px]">Connect your Xero login to pull this client's trial balance directly. You'll pick the client's organisation after signing in.</span>
                        <button data-testid="xero-connect-btn" onClick={connect} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm flex items-center gap-1.5">
                            <Cloud className="w-3.5 h-3.5" />Connect Xero
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function SchedulesBudgets({ engagement, reload }) {
    const [open, setOpen] = useState(true);
    const [cats, setCats] = useState(null);
    const [budgets, setBudgets] = useState([]);
    const [selCat, setSelCat] = useState(engagement.xero_tracking_category_id || "");
    const [links, setLinks] = useState(engagement.schedule_budget_links || {});
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const [tc, bg] = await Promise.all([
                api.get(`/engagements/${engagement.id}/xero/tracking-categories`),
                api.get(`/engagements/${engagement.id}/xero/budgets`),
            ]);
            setCats(tc.data.categories || []);
            setSelCat(tc.data.selected_id || "");
            setBudgets(bg.data.budgets || []);
            setLinks(bg.data.links || {});
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); setCats([]); }
        finally { setBusy(false); }
    };
    const toggle = () => { const n = !open; setOpen(n); if (n && cats === null) load(); };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    const chooseCat = async (id) => {
        setSelCat(id);
        const c = (cats || []).find((x) => x.id === id);
        try { await api.post(`/engagements/${engagement.id}/xero/tracking-category`, { id: id || null, name: c?.name || null }); toast.success(id ? `Schedules = "${c?.name}"` : "Tracking category cleared"); reload?.(); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    const setLink = (opt, bid) => {
        const b = budgets.find((x) => x.id === bid);
        setLinks((prev) => { const n = { ...prev }; if (bid) n[opt] = { budget_id: bid, budget_name: b?.name }; else delete n[opt]; return n; });
    };
    const saveLinks = async () => {
        try { await api.post(`/engagements/${engagement.id}/xero/budget-links`, { links }); toast.success("Budgets linked to schedules"); reload?.(); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    const cat = (cats || []).find((c) => c.id === selCat);

    return (
        <div className="mt-3 border border-brand/30 bg-brand/5 rounded-sm p-3" data-testid="xero-schedules-config">
            <button data-testid="xero-schedules-toggle" onClick={toggle} className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}<Layers className="w-4 h-4 text-brand" /> Step 2 · Set schedules &amp; budgets, then pull
            </button>
            <p className="text-[11px] text-muted-foreground mt-1 ml-6">Choose the Xero tracking category that represents your service-charge schedules — its options become your schedule names automatically. Then link each schedule to its Xero budget. Do this <b>before</b> pulling the trial balance.</p>
            {open && (
                <div className="mt-3 space-y-3">
                    {busy && cats === null ? (
                        <div className="text-xs text-muted-foreground flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading tracking categories & budgets…</div>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs text-muted-foreground">Which Xero tracking category represents your service-charge schedules?</span>
                                <select data-testid="xero-trackcat-select" value={selCat} onChange={(e) => chooseCat(e.target.value)} className="border border-input rounded-sm px-2 py-1.5 text-sm bg-white min-w-[220px]">
                                    <option value="">— none (whole-file budget) —</option>
                                    {(cats || []).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.options.length} options)</option>)}
                                </select>
                                <button data-testid="xero-schedules-reload" onClick={load} className="text-brand hover:underline text-xs">Reload</button>
                            </div>
                            {cat && (
                                <div className="bg-slate-50 border border-border rounded-sm">
                                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-display font-semibold text-muted-foreground border-b border-border">Link each schedule to its Xero budget</div>
                                    <table className="tbl">
                                        <thead><tr><th>Schedule (tracking option)</th><th className="w-72">Xero budget</th></tr></thead>
                                        <tbody>
                                            {cat.options.map((opt) => (
                                                <tr key={opt}>
                                                    <td className="text-xs font-medium">{opt}</td>
                                                    <td>
                                                        <select data-testid={`xero-budget-link-${opt}`} value={links[opt]?.budget_id || ""} onChange={(e) => setLink(opt, e.target.value)} className="w-full border border-input rounded-sm px-1.5 py-1 text-xs bg-white">
                                                            <option value="">— not linked —</option>
                                                            {budgets.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                            {cat.options.length === 0 && <tr><td colSpan={2} className="text-center text-muted-foreground py-3 italic text-xs">This tracking category has no active options.</td></tr>}
                                        </tbody>
                                    </table>
                                    <div className="px-3 py-2 border-t border-border flex justify-end">
                                        <button data-testid="xero-budget-links-save" onClick={saveLinks} className="bg-brand hover:bg-brand-hover text-white text-xs font-medium px-3 py-1.5 rounded-sm">Save budget links</button>
                                    </div>
                                </div>
                            )}
                            <p className="text-[11px] text-muted-foreground">When a schedule tracking category is set, a pull splits income &amp; expenditure per schedule (via Xero's P&amp;L per tracking option) and applies each schedule's linked budget. Balance-sheet accounts stay whole-file.</p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export function LeadSheets() {
    const { engagement } = useOutletContext();
    const [data, setData] = useState({ groups: {}, totals: {} });

    useEffect(() => {
        api.get(`/engagements/${engagement.id}/lead-sheets`).then((r) => setData(r.data)).catch(() => {});
    }, [engagement.id]);

    return (
        <div className="p-6 max-w-6xl" data-testid="lead-sheets-page">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-display mb-1">Lead schedules</div>
            <h2 className="font-display font-bold text-2xl tracking-tight mb-5">Grouped by category & schedule</h2>

            {Object.keys(data.groups).length === 0 && (
                <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">
                    Import a trial balance first — lead sheets pull from TB.
                </div>
            )}

            {Object.entries(data.groups).map(([cat, schs]) => (
                <div key={cat} className="bg-white border border-border rounded-sm mb-4">
                    <div className="px-4 py-2.5 border-b border-border font-display font-bold text-sm bg-slate-50 flex items-center">
                        {cat}
                        <div className="ml-auto font-num text-xs flex gap-4 text-muted-foreground">
                            <span>C: <span className={sumCat(data.totals[cat], "current_period") < 0 ? "text-num-neg" : "text-num-pos"}>{fmtGbp(sumCat(data.totals[cat], "current_period"))}</span></span>
                            <span>P: <span className={sumCat(data.totals[cat], "prior_period") < 0 ? "text-num-neg" : "text-num-pos"}>{fmtGbp(sumCat(data.totals[cat], "prior_period"))}</span></span>
                            <span>B: <span className={sumCat(data.totals[cat], "budget") < 0 ? "text-num-neg" : "text-num-pos"}>{fmtGbp(sumCat(data.totals[cat], "budget"))}</span></span>
                        </div>
                    </div>
                    {Object.entries(schs).map(([sch, rowsInSch]) => {
                        const t = data.totals[cat][sch];
                        return (
                            <div key={sch}>
                                <div className="px-4 py-1.5 text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground border-t border-border bg-slate-50/50">{sch}</div>
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th className="w-24">Code</th>
                                            <th>Account</th>
                                            <th className="text-right w-32">Current</th>
                                            <th className="text-right w-32">Prior</th>
                                            <th className="text-right w-32">Budget</th>
                                            <th className="text-right w-24">Var % vs Prior</th>
                                            <th className="text-right w-24">Var % vs Budget</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rowsInSch.map((r, i) => {
                                            const vP = variance(r.current_period, r.prior_period);
                                            const vB = variance(r.current_period, r.budget);
                                            return (
                                                <tr key={r.id || i}>
                                                    <td className="font-num text-xs">{r.account_code}</td>
                                                    <td>{r.account_name}</td>
                                                    <td className={`num ${r.current_period < 0 ? "num-neg" : ""}`}>{fmtGbp(r.current_period)}</td>
                                                    <td className={`num ${r.prior_period < 0 ? "num-neg" : ""}`}>{fmtGbp(r.prior_period)}</td>
                                                    <td className={`num ${r.budget < 0 ? "num-neg" : ""}`}>{fmtGbp(r.budget)}</td>
                                                    <td className={`num ${Math.abs(vP) >= 10 ? "text-risk-med font-semibold" : "text-muted-foreground"}`}>{fmtPct(vP)}</td>
                                                    <td className={`num ${Math.abs(vB) >= 10 ? "text-risk-med font-semibold" : "text-muted-foreground"}`}>{fmtPct(vB)}</td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="bg-slate-50 font-semibold">
                                            <td colSpan={2} className="uppercase text-[11px] tracking-widest text-muted-foreground font-display">Total {sch}</td>
                                            <td className={`num ${t.current_period < 0 ? "num-neg" : ""}`}>{fmtGbp(t.current_period)}</td>
                                            <td className={`num ${t.prior_period < 0 ? "num-neg" : ""}`}>{fmtGbp(t.prior_period)}</td>
                                            <td className={`num ${t.budget < 0 ? "num-neg" : ""}`}>{fmtGbp(t.budget)}</td>
                                            <td className="num text-muted-foreground">{fmtPct(variance(t.current_period, t.prior_period))}</td>
                                            <td className="num text-muted-foreground">{fmtPct(variance(t.current_period, t.budget))}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

function sumCat(t, k) {
    if (!t) return 0;
    return Object.values(t).reduce((s, v) => s + (v[k] || 0), 0);
}
function fmtGbp(n) {
    if (n === undefined || n === null || isNaN(n)) return "—";
    const v = Math.abs(n);
    return `${n < 0 ? "(" : ""}£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${n < 0 ? ")" : ""}`;
}
function variance(a, b) {
    if (!b) return 0;
    return ((a - b) / Math.abs(b)) * 100;
}
function fmtPct(v) {
    if (!isFinite(v) || v === 0) return "—";
    return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}
